import { useEffect, useRef, useState } from "react";

import { v4 as uuid } from "uuid";

import { sendMixpanelTracking, trackPageView } from "services";

import { checkCookieConsent, generateRandomNumber, getLocalKlimVar, isImpersonating } from "utils";

import { SOILSIGHT_ROUTE } from "consts";

import { Message as message } from "components";

export const getMobileOperatingSystem = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  let system = "android";

  if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
    system = "ios";
  }

  return system;
};

// Exact match ... `/campaign/soilsight-teaser` is not the funnel.
const isSoilSightPath = (pathname) => pathname === SOILSIGHT_ROUTE || pathname.startsWith(`${SOILSIGHT_ROUTE}/`);

// Frozen at first load ... flipping mid-session swaps Maps key / SignUp / API appType.
// SoilSight exempt so anonymous deep links survive LAN IPs and tunnels this guess calls "native".
// Host+viewport guess until build:app sets REACT_APP_BUILD_TARGET=app.
const isNativeShell = !window.location.host.includes("klim.") && window.innerWidth <= 768 && !isSoilSightPath(window.location.pathname);

export const getEnvironment = () => {
  const { host, pathname } = window.location;
  const env = {
    local: false,
    admin: false,
    staging: false,
    development: false,
    production: false,
    app: false,
    landing: false,
  };

  // Stay local until the bounce lands ... Sentry/RUM stay off. Exact host+port: Cordova may use http://localhost.
  if (host === "localhost:3000" || host === "127.0.0.1:3000") {
    env.local = true;
    return env;
  }
  if (host.includes(".local")) {
    env.local = true;
  }
  if (host.includes("admin.") || host.includes("admin-new.")) {
    env.admin = true;
  }
  if (host.includes("staging")) {
    env.staging = true;
  }
  if (host.includes("development") || host.includes("dev")) {
    env.development = true;
  }
  if (isNativeShell) {
    env.app = true;
  }
  if (host.includes("farms.") && host.includes(".eco")) {
    env.production = true;
  }
  if (!pathname.includes("")) {
    env.landing = true;
  }
  return env;
};

const getAppType = () => {
  const env = getEnvironment();
  const isMobile = window.innerWidth <= 768;
  const isSmallDesktop = window.innerWidth <= 1180;
  const isLargeDesktop = window.innerWidth > 1728; // width where the open side menu no longer overlaps content, with a nice margin

  return {
    mobile: isMobile,
    desktop: !isMobile,
    website: !env.app,
    app: env.app,
    smallDesktop: isSmallDesktop,
    largeDesktop: isLargeDesktop,
  };
};

export const getEnvironmentString = () => {
  const env = getEnvironment();

  if (env.production) {
    return "production";
  }
  if (env.staging) {
    return "staging";
  }
  if (env.development) {
    return "development";
  }
  if (env.admin) {
    return "admin";
  }
  if (env.app) {
    return "app";
  }
  return "unknown";
};

export const useAppType = () => {
  const [appType, setAppType] = useState(() => getAppType());

  const handleResize = () => {
    setAppType(getAppType());
  };

  useEffect(() => {
    // exclude the event listener in the app
    // opening the keyboard triggers it in android
    if (!appType.app) {
      window.addEventListener("resize", handleResize);
    }

    // Remove listener in cleanup function
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return appType;
};

// This function is needed to map the new appType object
// to the string system which was previously used,
// because the api expects a string (rewriting the backend does not seem pragmatic to me)
export const mapAppTypeToString = (appType) => {
  if (appType.app) {
    return "mobileApp";
  }

  if (appType.website && appType.mobile) {
    return "mobileWebsite";
  }

  return "desktopWebsite";
};

/** @param {Record<string, unknown> | null} [state] navigate options, e.g. `{ state }` */
export const goTo = (e, navigate, location, forceBrowser = false, state = null) => {
  if (e && e.preventDefault) {
    e.preventDefault();
    e.stopPropagation();
  }

  if (forceBrowser) {
    return window.open(location);
  }
  return state ? navigate(location, state) : navigate(location);
};

export const copyToClipboard = async (text) => {
  // Navigator clipboard api needs a secure context (https)
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (_err) {
      return false;
    }
  }
  // Use the 'out of viewport hidden text area' trick
  const input = document.createElement("input");
  input.type = "text";
  input.value = text;

  // Move textarea out of the viewport so it's not visible
  input.style.position = "absolute";
  input.style.left = "-999999px";

  document.body.prepend(input);
  input.select();

  let success = false;
  try {
    success = document.execCommand("copy");
  } catch (_err) {
    success = false;
  } finally {
    input.remove();
  }
  return success;
};

export const sortArrayOfObjectsAsc = (arr, key) => arr.sort((a, b) => parseFloat(a[key]) - parseFloat(b[key]));

export const sortArrayOfObjectsDesc = (arr, key) => arr.sort((a, b) => parseFloat(b[key]) - parseFloat(a[key]));

export const triggerLeaves = (leafCount) => {
  let size;
  let rotation;
  let el;

  const removeLeafContainer = () => {
    setTimeout(() => {
      const leafContainer = document.querySelector(".explodingLeaves");
      if (leafContainer) {
        leafContainer.remove();
      }
    }, 500);
  };

  const triggerLeafAnimation = (i, transform) => {
    const group = leafCount / 4;
    const el = document.getElementById(`leaf-${i}`);

    if (i <= group && document.getElementById(`leaf-${i}`)) {
      el.style.top = -200;
      el.style.left = generateRandomNumber(0, document.querySelector(".explodingLeaves").getBoundingClientRect().width);
      el.style.transform = transform;
      el.style.filter = `blur(${generateRandomNumber(0, 3)}px)`;
    } else if (i <= group * 2 && document.getElementById(`leaf-${i}`)) {
      el.style.top = generateRandomNumber(0, document.querySelector(".explodingLeaves").getBoundingClientRect().height);
      el.style.left = document.querySelector(".explodingLeaves").getBoundingClientRect().width + 200;
      el.style.transform = transform;
      el.style.filter = `blur(${generateRandomNumber(0, 3)}px)`;
    } else if (i <= group * 3 && document.getElementById(`leaf-${i}`)) {
      el.style.top = document.querySelector(".explodingLeaves").getBoundingClientRect().height + 200;
      el.style.left = generateRandomNumber(0, document.querySelector(".explodingLeaves").getBoundingClientRect().width);
      el.style.transform = transform;
      el.style.filter = `blur(${generateRandomNumber(0, 3)}px)`;
    } else if (i <= group * 4 && document.getElementById(`leaf-${i}`)) {
      el.style.top = generateRandomNumber(0, document.querySelector(".explodingLeaves").getBoundingClientRect().height);
      el.style.left = -200;
      el.style.transform = transform;
      el.style.filter = `blur(${generateRandomNumber(0, 3)}px)`;
    }
  };

  if (document.querySelector("#badge") && document.querySelector("#badge").getBoundingClientRect()) {
    for (let i = 0; i < leafCount; i += 1) {
      el = document.getElementById(`leaf-${i}`);

      size = generateRandomNumber(1, 2);
      rotation = generateRandomNumber(30, 135);

      el.style.transform = `translate3d(0, 0, 0) scale(${size}) rotate(${rotation}deg)`;
      el.style.left = document.querySelector("#badge").getBoundingClientRect().left;
      el.style.top = document.querySelector("#badge").getBoundingClientRect().top;
      el.style.opacity = 1;

      setTimeout(
        (size, rotation) => {
          triggerLeafAnimation(i, `translate3d(0, 0, 0) scale(${size * generateRandomNumber(2, 5)}) rotate(${rotation * generateRandomNumber(1.25, 2)}deg)`);
          removeLeafContainer();
        },
        generateRandomNumber(50, 175)
      );
    }
  } else {
    setTimeout(() => {
      triggerLeaves(leafCount);
    }, 100);
  }
};

export const trackMixpanel = (name, data) => {
  // No client analytics while impersonating: the tracking routes 403 (off the allow list), and the
  // admin's activity shouldn't land on the farmer's analytics.
  if (isImpersonating()) return;

  const env = getEnvironment();
  // Mixpanel is non-essential analytics: only track once the user has given cookie consent.
  if ((env.production || env.app) && name && !env.admin && checkCookieConsent()) {
    const sessionId = getLocalKlimVar("sessionId");

    sendMixpanelTracking({ sessionId, path: window.location.pathname, name, data });
  }
};

export const incompleteMessage = (content) => {
  message({ content, icon: "error" });
};

export const splitByUppercase = (string) => string?.split(/(?=[A-Z])/).join(" ") ?? "";

export const recordPageView = (type, page) => {
  if (isImpersonating()) return;
  // Page-view tracking is non-essential analytics: only record once the user has given cookie consent.
  if (!checkCookieConsent()) return;
  trackPageView({ type: type, page: page });
};

export const scrollPage = (element) => {
  setTimeout(() => {
    if (element) {
      element.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    } else if (window) {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    }
  }, 150);
};

export const scrollPageTo = (top, behavior = "smooth") => {
  if (window) {
    window.scrollTo({ top, left: 0, behavior });
  }
};

export const scrollPageBy = (top, behavior = "smooth") => {
  if (window) {
    window.scrollBy({ top, left: 0, behavior });
  }
};

export const generateRandomId = () => uuid();

export const useScroll = () => {
  const [scrollPosition, setScrollPosition] = useState(0);
  const ref = useRef(null);

  const handleScroll = (position) => {
    setScrollPosition(position);
  };

  useEffect(() => {
    if (!ref.current) {
      return;
    }

    ref.current.goTo(scrollPosition);
  }, [scrollPosition]);

  return {
    handleScroll,
    ref,
    scrollPosition,
  };
};

export const getFarmIdFromLocalStorage = () => getLocalKlimVar("farmId");

// you can set feature flags at the URL level
// multiple items are acceptible
// for example: ?feature=submission&feature=tasks
export const checkFeatureFlag = (flag) => {
  const featureFlags = new URLSearchParams(window.location.search).getAll("feature");
  return featureFlags.includes(flag);
};

export const scrollIntoViewWithObserver = (elementRef, options = { behavior: "smooth" }, intersectionOptions = { threshold: 0.95 }) => {
  elementRef.current?.scrollIntoView(options);

  return new Promise((resolve) => {
    const intersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (entry.isIntersecting) {
        resolve(true);
        intersectionObserver.unobserve(elementRef.current);
      }
    }, intersectionOptions);

    intersectionObserver.observe(elementRef.current);
  });
};

export const parseInputRules = (rules, message) =>
  rules || message ? [{ message: message || undefined, ...(rules?.[0] || null) }, ...(rules || []).slice(1)] : undefined;
